import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { usePushNotifications } from './usePushNotifications';
import { clubCommunityOptimizedService } from '@/services/api/club-community-optimized.service';
import { clubCommunityService } from '@/services/api/club-community.service';
import type {
  ClubChannel,
  ClubChannelMessage,
  SendMessageRequest,
  TypingIndicator,
  UserProfile
} from '@/types/club-community';

export interface UseClubCommunityOptimizedOptions {
  clubId: string;
  autoSubscribe?: boolean;
}

export interface UseClubCommunityOptimizedReturn {
  // Channels
  channels: ClubChannel[];
  channelsLoading: boolean;
  channelsError: string | null;
  activeChannel: string | null;
  
  // Messages
  messages: ClubChannelMessage[];
  messagesLoading: boolean;
  messagesError: string | null;
  
  // Pagination
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  
  // Members
  clubMembers: UserProfile[];
  membersLoading: boolean;
  
  // Typing indicators
  typingUsers: TypingIndicator[];
  
  // Actions
  setActiveChannel: (channelId: string) => void;
  sendMessage: (data: SendMessageRequest) => Promise<void>;
  markChannelRead: (channelId: string) => Promise<void>;
  refetchChannels: () => void;
  refetchMessages: () => void;
  
  // Reactions
  addReaction: (messageId: string, emoji: string) => Promise<void>;
  removeReaction: (messageId: string, emoji: string) => Promise<void>;
}

/**
 * Optimized club community hook using React Query
 * Eliminates N+1 queries and provides better caching
 */
export function useClubCommunityOptimized(options: UseClubCommunityOptimizedOptions): UseClubCommunityOptimizedReturn {
  const { user } = useAuth();
  const { clubId, autoSubscribe = true } = options;
  const queryClient = useQueryClient();
  const { showMentionNotification } = usePushNotifications();

  // Helper function to check for mentions and send notifications
  const handleMentionNotifications = useCallback(async (
    message: ClubChannelMessage, 
    channelData: ClubChannel | undefined,
    clubMembers: UserProfile[]
  ) => {
    if (!message.mentions || message.mentions.length === 0) return;
    if (!channelData || !user) return;
    
    // Don't send notifications for own messages
    if (message.sender_id === user.id) return;

    const authorProfile = clubMembers.find(member => member.id === message.sender_id);
    if (!authorProfile) return;

    // Send notification to each mentioned user (except the author)
    for (const mention of message.mentions) {
      if (mention.mentioned_user_id === user.id) {
        // Current user was mentioned - show notification
        await showMentionNotification({
          mentionedBy: authorProfile.display_name || 'Someone',
          clubName: 'Golf Club', // TODO: Get actual club name
          channelName: channelData.name,
          messagePreview: message.content?.substring(0, 100) || '',
          clubId: clubId,
          channelId: channelData.id
        });
      }
    }
  }, [showMentionNotification, user, clubId]);

  // Local state with performance optimization
  const [activeChannel, setActiveChannelState] = useState<string | null>(null);
  const [typingUsers, setTypingUsers] = useState<TypingIndicator[]>([]);

  // Keep a live ref to activeChannel so long-lived subscription handlers can
  // read the current value without re-subscribing every time it switches.
  const activeChannelRef = useRef<string | null>(null);
  activeChannelRef.current = activeChannel;

  // Memoize stable values to prevent unnecessary re-renders
  const stableClubId = useMemo(() => clubId, [clubId]);
  const stableAutoSubscribe = useMemo(() => autoSubscribe, [autoSubscribe]);

  // Refs for subscriptions
  const channelSubscription = useRef<any>(null);
  const reactionSubscription = useRef<any>(null);
  const channelListSubscription = useRef<any>(null);
  const clubMessagesSubscription = useRef<any>(null);

  // Optimized channels query with better caching strategy
  const {
    data: channels = [],
    isLoading: channelsLoading,
    error: channelsQueryError,
    refetch: refetchChannels
  } = useQuery({
    queryKey: ['club-channels', stableClubId],
    queryFn: async () => {
      if (!user || !stableClubId) return [];
      const response = await clubCommunityOptimizedService.getClubChannelsOptimized(stableClubId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to load channels');
      }
      return response.data;
    },
    enabled: !!user && !!stableClubId,
    staleTime: 30 * 1000, // 30s — unread counts must stay fresh
    cacheTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnMount: true,
    refetchOnWindowFocus: true // Re-check unread counts when user returns to the tab
  });

  const channelsError = channelsQueryError?.message || null;

  // Messages query with infinite scrolling
  const {
    data: messagesData,
    isLoading: messagesLoading,
    error: messagesQueryError,
    refetch: refetchMessages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ['club-messages', activeChannel],
    queryFn: async ({ pageParam = undefined }) => {
      if (!user || !activeChannel) return [];
      const response = await clubCommunityOptimizedService.getChannelMessagesOptimized(
        activeChannel, 
        30, // Reduced limit per page for better performance
        pageParam // before cursor
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to load messages');
      }
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      // Use the oldest message's created_at as the before-cursor to load earlier messages
      // pages[0] is chronological (oldest→newest after .reverse()), so index 0 is oldest
      if (lastPage.length === 0) return undefined;
      return lastPage[0]?.created_at;
    },
    enabled: !!user && !!activeChannel,
    staleTime: 30 * 1000, // Cache for 30 seconds (messages change more frequently)
    cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    retry: 2,
    retryDelay: 1000,
    refetchOnWindowFocus: false // Don't refetch on window focus
  });

  // Optimized message flattening with better performance
  const messages = useMemo(() => {
    if (!messagesData?.pages || messagesData.pages.length === 0) return [];
    
    // Early return for single page (most common case)
    if (messagesData.pages.length === 1) {
      return messagesData.pages[0] || [];
    }
    
    // Pages are ordered from newest to oldest
    // Each page contains messages in chronological order (oldest to newest)
    // So we need to reverse the pages order and then flatten
    const reversedPages = [...messagesData.pages].reverse();
    const allMessages = reversedPages.flat();
    
    // Deduplicate messages by ID to prevent React key conflicts
    const seenIds = new Set();
    const deduplicatedMessages = allMessages.filter(message => {
      if (seenIds.has(message.id)) {
        return false;
      }
      seenIds.add(message.id);
      return true;
    });
    
    return deduplicatedMessages;
  }, [messagesData]);

  const messagesError = messagesQueryError?.message || null;

  // Club members query for mentions
  const {
    data: clubMembers = [],
    isLoading: membersLoading
  } = useQuery({
    queryKey: ['club-members', clubId],
    queryFn: async () => {
      if (!user || !clubId) return [];
      const response = await clubCommunityOptimizedService.getClubMembers(clubId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to load club members');
      }
      return response.data;
    },
    enabled: !!user && !!clubId,
    staleTime: 60000, // Cache for 1 minute
    retry: 2
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (data: SendMessageRequest) => {
      const response = await clubCommunityOptimizedService.sendMessageOptimized(data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to send message');
      }
      return response.data;
    },
    onSuccess: async (newMessage) => {
      // Optimistically update the infinite query cache
      queryClient.setQueryData(['club-messages', activeChannelRef.current], (oldData: any) => {
        if (!oldData?.pages) {
          // If no existing data, create initial structure
          return {
            pages: [[newMessage]],
            pageParams: [undefined]
          };
        }
        
        // Append new message to the end of the first page (pages[0] is chronological oldest→newest)
        const newPages = [...oldData.pages];
        if (newPages[0]) {
          newPages[0] = [...newPages[0], newMessage];
        } else {
          newPages[0] = [newMessage];
        }

        return {
          ...oldData,
          pages: newPages
        };
      });

      // Invalidate channels to update last message
      queryClient.invalidateQueries({ queryKey: ['club-channels', clubId] });
      
      // Note: Mention notifications should be handled by real-time subscriptions
      // for the mentioned users, not by the sender. This will be implemented
      // when we add proper real-time message subscriptions.
    },
    onError: (error) => {
      console.error('Failed to send message:', error);
      // Could show a toast notification here
    }
  });

  // Auto-select first channel when channels load
  useEffect(() => {
    if (!activeChannel && channels.length > 0) {
      setActiveChannelState(channels[0].id);
    }
  }, [channels, activeChannel]);

  // Zero the unread badge immediately whenever the active channel changes —
  // covers both auto-select on load and manual channel switches.
  useEffect(() => {
    if (!activeChannel || !user?.id) return;
    queryClient.setQueryData(['club-channels', clubId], (oldChannels: ClubChannel[] = []) =>
      oldChannels.map(channel =>
        channel.id === activeChannel ? { ...channel, unread_count: 0 } : channel
      )
    );
    // Invalidate the /club page's total-unread query so its badge clears immediately
    queryClient.invalidateQueries({ queryKey: ['channel-unread', clubId] });
    clubCommunityService.updateLastRead(activeChannel, user.id);
  }, [activeChannel, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set active channel function
  const setActiveChannel = (channelId: string) => {
    // Clear typing users when switching channels
    setTypingUsers([]);
    
    // Update active channel immediately for instant UI response
    setActiveChannelState(channelId);
    
    // Messages will be fetched automatically by React Query
  };

  // Send message function
  const sendMessage = async (data: SendMessageRequest) => {
    await sendMessageMutation.mutateAsync(data);
  };

  // Mark a channel as read: zero the badge instantly via optimistic update,
  // then persist last_read_at. Invalidates the /club page's total-unread query
  // so that card badge clears immediately too.
  const markChannelRead = useCallback(async (channelId: string) => {
    if (!user?.id || !channelId) return;

    queryClient.setQueryData(['club-channels', clubId], (oldChannels: ClubChannel[] = []) =>
      oldChannels.map(channel =>
        channel.id === channelId ? { ...channel, unread_count: 0 } : channel
      )
    );
    queryClient.invalidateQueries({ queryKey: ['channel-unread', clubId] });

    await clubCommunityService.updateLastRead(channelId, user.id);
  }, [queryClient, clubId, user?.id]);

  // Reaction mutations
  const addReactionMutation = useMutation({
    mutationFn: async ({ messageId, emoji }: { messageId: string; emoji: string }) => {
      const response = await clubCommunityOptimizedService.addReaction(messageId, emoji);
      if (!response.success) {
        throw new Error(response.error || 'Failed to add reaction');
      }
      return response.data;
    },
    onSuccess: (_, { messageId, emoji }) => {
      // Optimistically update the infinite query cache
      queryClient.setQueryData(['club-messages', activeChannelRef.current], (oldData: any) => {
        if (!oldData?.pages) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page: ClubChannelMessage[]) =>
            page.map(message =>
              message.id === messageId
                ? {
                    ...message,
                    reaction_counts: {
                      ...message.reaction_counts,
                      [emoji]: (message.reaction_counts?.[emoji] || 0) + 1
                    },
                    user_has_reacted: {
                      ...message.user_has_reacted,
                      [emoji]: true
                    }
                  }
                : message
            )
          )
        };
      });
    },
    onError: (error) => {
      console.error('Failed to add reaction:', error);
    }
  });

  const removeReactionMutation = useMutation({
    mutationFn: async ({ messageId, emoji }: { messageId: string; emoji: string }) => {
      const response = await clubCommunityOptimizedService.removeReaction(messageId, emoji);
      if (!response.success) {
        throw new Error(response.error || 'Failed to remove reaction');
      }
      return response.data;
    },
    onSuccess: (_, { messageId, emoji }) => {
      // Optimistically update the infinite query cache
      queryClient.setQueryData(['club-messages', activeChannelRef.current], (oldData: any) => {
        if (!oldData?.pages) return oldData;
        
        return {
          ...oldData,
          pages: oldData.pages.map((page: ClubChannelMessage[]) =>
            page.map(message =>
              message.id === messageId
                ? {
                    ...message,
                    reaction_counts: {
                      ...message.reaction_counts,
                      [emoji]: Math.max((message.reaction_counts?.[emoji] || 1) - 1, 0)
                    },
                    user_has_reacted: {
                      ...message.user_has_reacted,
                      [emoji]: false
                    }
                  }
                : message
            )
          )
        };
      });
    },
    onError: (error) => {
      console.error('Failed to remove reaction:', error);
    }
  });

  // Reaction functions
  const addReaction = async (messageId: string, emoji: string) => {
    await addReactionMutation.mutateAsync({ messageId, emoji });
  };

  const removeReaction = async (messageId: string, emoji: string) => {
    await removeReactionMutation.mutateAsync({ messageId, emoji });
  };

  // Real-time subscriptions
  useEffect(() => {

    if (!autoSubscribe || !activeChannel) {
      return;
    }

    // Subscribe to new messages
    channelSubscription.current = clubCommunityOptimizedService.subscribeToChannelOptimized(
      activeChannel,
      async (message: ClubChannelMessage) => {
        // Update the messages cache with new message
        queryClient.setQueryData(['club-messages', activeChannel], (oldData: any) => {
          if (!oldData?.pages) {
            // If no existing data, create initial structure
            return {
              pages: [[message]],
              pageParams: [undefined]
            };
          }
          
          // Check for duplicates across all pages
          const isDuplicate = oldData.pages.some((page: ClubChannelMessage[]) =>
            page.some(msg => msg.id === message.id)
          );
          if (isDuplicate) return oldData;
          
          // Append new message to the end of the first page (pages[0] is chronological oldest→newest)
          const newPages = [...oldData.pages];
          if (newPages[0]) {
            newPages[0] = [...newPages[0], message];
          } else {
            newPages[0] = [message];
          }

          return {
            ...oldData,
            pages: newPages
          };
        });
        
        // Update the channels cache: show new last message and bump unread count.
        // The active channel stays at 0 (the user is looking at it); other channels
        // get an incremented badge immediately instead of waiting for the staleTime.
        queryClient.setQueryData(['club-channels', clubId], (oldChannels: ClubChannel[] = []) =>
          oldChannels.map(channel =>
            channel.id === message.channel_id
              ? {
                  ...channel,
                  last_message: message,
                  unread_count: channel.id === activeChannel
                    ? 0
                    : (channel.unread_count || 0) + 1
                }
              : channel
          )
        );

        // Handle mention notifications for the current user
        const currentChannel = channels.find(c => c.id === activeChannel);
        await handleMentionNotifications(message, currentChannel, clubMembers);
      }
    );

    // Subscribe to reaction updates
    reactionSubscription.current = clubCommunityOptimizedService.subscribeToReactionsOptimized(
      activeChannel,
      (payload) => {
        // Refetch messages to get updated reaction data
        queryClient.invalidateQueries({ queryKey: ['club-messages', activeChannel] });
      }
    );

    return () => {
      if (channelSubscription.current) {
        clubCommunityOptimizedService.unsubscribeFromChannel(activeChannel);
        channelSubscription.current = null;
      }
      if (reactionSubscription.current) {
        clubCommunityOptimizedService.unsubscribeFromReactions(activeChannel);
        reactionSubscription.current = null;
      }
    };
  }, [autoSubscribe, activeChannel, queryClient, clubId, channels, clubMembers, handleMentionNotifications]);

  // Subscribe to channel list changes for real-time updates
  useEffect(() => {
    if (!autoSubscribe) return;

    channelListSubscription.current = clubCommunityOptimizedService.subscribeToClubChannelsOptimized(
      clubId,
      (updatedChannels: ClubChannel[]) => {
        // Update the channels cache
        queryClient.setQueryData(['club-channels', clubId], updatedChannels);
      }
    );

    return () => {
      if (channelListSubscription.current) {
        clubCommunityOptimizedService.unsubscribeFromClubChannelsOptimized(clubId);
        channelListSubscription.current = null;
      }
    };
  }, [autoSubscribe, clubId, queryClient]);

  // Stable, sorted list of channel IDs so the club-wide subscription only
  // re-subscribes when the actual set of channels changes (not on every
  // channels-array reference change, e.g. unread_count/last_message patches).
  const channelIdsKey = useMemo(
    () => channels.map(c => c.id).sort().join(','),
    [channels]
  );

  // Keep a ref to the current set of valid channel IDs so the subscription
  // callback can filter without needing to re-subscribe every time channels load.
  const channelIdsSetRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    channelIdsSetRef.current = new Set(channelIdsKey.split(',').filter(Boolean));
  }, [channelIdsKey]);

  // Club-wide message subscription: bump unread badges for channels the user
  // is NOT currently viewing, in real time. The active channel keeps its own
  // per-channel subscription (above), so we skip it here, and we ignore the
  // user's own messages.
  // Subscribe as soon as clubId is known — don't wait for channelIds to load.
  // The callback filters against channelIdsSetRef which updates without re-subscribing.
  const userIdRef = useRef(user?.id);
  useEffect(() => { userIdRef.current = user?.id; }, [user?.id]);

  useEffect(() => {
    if (!autoSubscribe || !clubId) return;

    clubMessagesSubscription.current = clubCommunityOptimizedService.subscribeToClubMessagesOptimized(
      clubId,
      ['*'], // sentinel — service will use the ref-based filter below
      ({ channel_id, sender_id }) => {
        // Filter to channels belonging to this club (ref updated as channels load).
        if (!channelIdsSetRef.current.has(channel_id)) return;
        // The open channel is handled by its own subscription + markChannelRead.
        if (channel_id === activeChannelRef.current) return;
        // Don't bump for messages the current user sent.
        if (sender_id && sender_id === userIdRef.current) return;

        queryClient.setQueryData(['club-channels', clubId], (oldChannels: ClubChannel[] = []) =>
          oldChannels.map(channel =>
            channel.id === channel_id
              ? { ...channel, unread_count: (channel.unread_count || 0) + 1 }
              : channel
          )
        );
      }
    );

    return () => {
      if (clubMessagesSubscription.current) {
        clubCommunityOptimizedService.unsubscribeFromClubMessages(clubId);
        clubMessagesSubscription.current = null;
      }
    };
  }, [autoSubscribe, clubId, queryClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clubCommunityOptimizedService.unsubscribeAll();
    };
  }, []);

  return {
    // Channels
    channels,
    channelsLoading,
    channelsError,
    activeChannel,
    
    // Messages
    messages,
    messagesLoading,
    messagesError,
    
    // Pagination
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    
    // Members
    clubMembers,
    membersLoading,
    
    // Typing indicators
    typingUsers,
    
    // Actions
    setActiveChannel,
    sendMessage,
    markChannelRead,
    refetchChannels,
    refetchMessages,
    
    // Reactions
    addReaction,
    removeReaction
  };
}